import * as React from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import _ from "lodash";
import { useState } from "react";
import { toast } from "react-toastify";

import { Text } from "src/library/components/Text";
import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import {
  formatPrice,
  priceInPence,
  toFixed
} from "src/core/prices/utils";
import { TransactionStatus } from "src/modules/payment/api/transactions.api";
import { useCurrentUser } from "src/core/users/hooks";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import Input from "src/library/components/Forms/Input";
import { InputSub } from "src/pages/Admin/Products/Product/CreateProductPage";
import { Button } from "src/library/components/Button";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import {
  CheckboxLabel,
  CheckboxStyled,
  FormRow
} from "src/library/components/Forms";

import {
  ITransaction,
  TransactionType
} from "../../api/transactions.api";
import { transactionTypeToHuman } from "../../utils";
import { TransactionStatusText } from "../TransactionStatusText";
import { usePaymentApi } from "../../api/payment.api";

interface TransactionsModalProps extends Omit<BasicModalProps, "title"> {
  transactions?: ITransaction[];
  orderId: number;
}

/**
 * Transaction
 */
const TransactionsModal: React.ReactFCWithChildren<TransactionsModalProps> = ({
  transactions,
  open,
  onDismiss,
  orderId
}) => {
  const [ { currentRole } ] = useCurrentUser();
  const hasPermission = useCheckUserPermission();
  const [ refundOrderRes, refundOrderReq ] = usePaymentApi("REFUND_ORDER");
  const [ refundValue, setRefundValue ] = useState("0.00");
  const [ refundAllChecked, setRefundAllChecked ] = useState(false);
  const [ refundError, setRefundError ] = useState<string | null>();

  const currentOrderBalance = React.useMemo(() => {
    if (!transactions) {
      return 0;
    }

    return _.reduce(transactions, (prev, currentTransaction) => {
      if (![ TransactionStatus.SUCCEEDED, TransactionStatus.REQUIRES_CAPTURE ].includes(currentTransaction.status)) {
        return prev;
      }

      if (currentTransaction.type === TransactionType.CHARGE) {
        return prev + currentTransaction.amount;
      } else {
        return prev - currentTransaction.amount;
      }
    }, 0);
  }, [ transactions ]);

  React.useEffect(() => {
    if (refundOrderRes.error) {
      setRefundError(refundOrderRes.error.message);
    } else if (refundOrderRes.data) {
      toast.success("Refund applied");
      window.location.reload();
    }
  }, [ refundOrderRes.data, refundOrderRes.error ]);

  /**
   * Applies a refund or sets an error if the amount is too much
   */
  const applyRefund = React.useCallback(() => {
    const refundPriceInPence = priceInPence(parseFloat(refundValue));

    if (refundPriceInPence < 0) {
      setRefundError("Refund must be greater than £0");
    } else if (refundPriceInPence > priceInPence(currentOrderBalance)) {
      setRefundError(`Cannot refund more then the original order amount: ${formatPrice(currentOrderBalance, 2)}`);
    } else {
      refundOrderReq({
        refundAmount: refundPriceInPence,
        orderId
      });
    }
  }, [
    currentOrderBalance,
    refundOrderReq,
    refundValue,
    orderId
  ]);

  const handleRefundInputChange = React.useCallback((newValue?: string) => {
    if (newValue) {
      if (priceInPence(parseFloat(newValue)) > priceInPence(currentOrderBalance)) {
        setRefundValue(toFixed(currentOrderBalance, 2));
      } else {
        setRefundValue(newValue ? parseFloat(newValue).toFixed(2) : "0.00");
      }
    }
  }, [ currentOrderBalance ]);

  return (
    <StyledTransactionsModal
      title="Transactions"
      open={open}
      onDismiss={onDismiss}
    >
      {!transactions || transactions.length === 0 ? (
        <Text>
          No transaction data available.
        </Text>
      ) : (
        <>
          <Text
            className="total-paid"
            as="h4"
          >
            {`${formatPrice(currentOrderBalance, 2)}`}
          </Text>

          <ul className="transactions">
            {transactions.map(transaction => (
              <li
                key={transaction.id}
                className="transaction"
              >
                <div className="transaction-content">
                  <div className="row">
                    <div className="row-inner value-wrapper">
                      <Text className="type">
                        {transactionTypeToHuman(transaction.type)}
                      </Text>
                    </div>

                    <div className="row-inner date-wrapper">

                      <Text className="date">
                        {moment.utc(transaction.createdAt).local().format("DD MMM YY")}
                      </Text>
                    </div>

                    <div className="row-inner status-wrapper">
                      <TransactionStatusText
                        className="status"
                        status={transaction.status}
                      />
                    </div>

                    <div className="row-inner price-wrapper">

                      <Text className="price">
                        {`${transaction.type === TransactionType.CHARGE ? "" : "-"} ${formatPrice(transaction.amount)}`}
                      </Text>
                    </div>
                  </div>

                  {transaction.errorReason && (
                    <div className="row error">
                      <Text
                        className="value"
                        color="error"
                      >
                        {transaction.errorReason.message}
                      </Text>
                    </div>
                  )}

                  {transaction.message && (
                    <div className="row message">
                      <Text
                        weight="bold"
                        className="value"
                      >
                        {transaction.message}
                      </Text>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {currentRole?.type === "admin" && hasPermission("amend-transaction") &&
          (
            <div className="refunds-form">
              <Text as="h3">
                Apply Refund
              </Text>

              {refundError && (
                <ErrorNotice
                  message={refundError}
                  type="error"
                />
              )}

              <FormRow>
                <Input
                  label="Refund amount"
                  disabled={refundAllChecked}
                  Prepend={(
                    <InputSub
                      width="40"
                      borderright="true"
                    >
                      <p>
                        £
                      </p>
                    </InputSub>
                  )}
                  as="input"
                  name="refundAmount"
                  handleChange={e => handleRefundInputChange(e.currentTarget.value)}
                  value={refundValue || "0.00"}
                  inputType="number"
                  step="1.00"
                />
              </FormRow>

              <FormRow>
                <CheckboxLabel
                  for="refund-all"
                  className="refund-all"
                  label="Refund Full Amount"
                >
                  <CheckboxStyled
                    onChange={() => {
                      setRefundAllChecked(currRefundChecked => {
                        if (currRefundChecked) {
                          setRefundValue("0.00");

                          return false;
                        } else {
                          setRefundValue(toFixed(currentOrderBalance, 2));

                          return true;
                        }
                      });
                    }}
                    name="refund-all"
                    checked={refundAllChecked}
                  />
                </CheckboxLabel>
              </FormRow>

              <FormRow>
                <Button
                  loading={refundOrderRes.pending}
                  onClick={() => {
                    applyRefund();
                  }}
                >
                  Submit Refund
                </Button>
              </FormRow>
            </div>
          )}
        </>
      )}
    </StyledTransactionsModal>
  );
};

export default TransactionsModal;

const StyledTransactionsModal = styled(BasicModal)`
  ${({ theme }) => css`

    .total-paid {
      display: block;
      width: 100%;
      text-align: right;
    }

    .refunds-form {
      margin-top: ${theme.spacing.margins.md};
      padding-top: ${theme.spacing.margins.md};
      position: relative;

      &:before {
        content: "";
        height: 2px;
        background: ${theme.colors.lightGrey};
        position: absolute;
        width: 100%;
        top: 0;
        left: 0;
      }
    }

    .transactions {

      .transaction {
        margin-top: ${theme.spacing.margins.sm};
        padding-top: ${theme.spacing.margins.sm};
        border-top: 0.1rem solid ${theme.colors.lightestGrey};

        &:first-child {
          margin-top: 0;
          padding-top: 0;
        }

        .transaction-header {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .date,
          .amount {
            font-size: ${theme.fonts.sizes.h4};
            font-weight: ${theme.fonts.weights.bold};
          }
        }

        .transaction-content {
          padding-top: ${theme.spacing.margins.sm};

          .row {
            display: flex;
            width: 100%;
            padding-top: 0.2rem;

            .row-inner {
              width: 45%;

              &.price-wrapper {
                width: 20%
              }
            }

            .price {
              display: block;
              width: 100%;
              text-align: right;
            }

            .type {
              text-transform: capitalize;
            }

            &:first-of-type {
              padding-top: 0;
            }

            .label,
            .value {
              display: block;
            }

            .label {
              padding-right: 0.6rem;
            }

            a.value {
              transition: ${theme.transition.quickest};;
              color: ${theme.colors.primaryDark};

              &:hover {
                color: ${theme.colors.primaryDark};
              }
            }

            &.message, &.error  {
              font-style: italic;
              margin-top: ${theme.spacing.margins.sm};
            }
          }
        }
      }
    }
  `};
`;
